import Reveal from '../Components/Reveal';
import '../styles/Products.css';
import platformImg from '../Assets/cooling tower.jpg';
import qcImg from '../Assets/qc.jpg';
import documentsImg from '../Assets/documents.jpg';
import energyWindImg from '../Assets/energy_wind.avif';
import analyticsDashboardImg from '../Assets/analytics dashboard.png';
import docunestWorkingImg from '../Assets/Working with DocuNest.AI at modern table.png';

const Products = () => (
  <>
    {/* ── Section 1: Hero ──────────────────────────────────────── */}
    <section className="ew-prod-hero">
      <div className="ew-prod-hero__inner">
        <div className="ew-prod-hero__card">
          <img
            className="ew-prod-hero__img"
            src={platformImg}
            alt="Intelligent platforms for energy data sovereignty"
          />
          <div className="ew-prod-hero__overlay">
            <Reveal>
              <p className="ew-prod-hero__eyebrow">The Teczo Ecosystem</p>
              <h1 className="ew-prod-hero__heading">
                Intelligent Platforms for{' '}
                <span className="ew-prod-hero__heading-muted">Energy Data Sovereignty</span>
              </h1>
              <p className="ew-prod-hero__sub">
                Teczo EnergyWorks builds purpose-designed software for the energy sector —
                transforming unstructured data chaos into structured intelligence, and
                connecting document AI, OSDU data management, and immersive visualization
                into a unified operational platform.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* ── Section 2: DocuNest.AI Intro ─────────────────────────── */}
    <section className="ew-prod-docunest">
      <div className="ew-prod-docunest__inner">
        <div className="ew-prod-docunest__grid">
          {/* Left — text (order-2 on mobile, order-1 on md) */}
          <Reveal style={{ order: 1 }}>
            <div>
              <p className="ew-prod-docunest__eyebrow">Document Intelligence</p>
              <h2 className="ew-prod-docunest__heading">DocuNest.AI</h2>
              <p className="ew-prod-docunest__body">
                DocuNest.AI is Teczo's purpose-built document intelligence engine for
                the energy sector. It transforms unstructured, legacy documents into
                structured, queryable knowledge — unlocking value buried in decades of
                reports, well logs, and regulatory filings.
              </p>
              <div className="ew-prod-docunest__cards">
                <div className="ew-prod-docunest__card">
                  <p className="ew-prod-docunest__card-label">Pain Point</p>
                  <p className="ew-prod-docunest__card-text">
                    Energy companies lose millions annually to inaccessible or
                    misclassified documents buried across legacy systems and file shares.
                  </p>
                </div>
                <div className="ew-prod-docunest__card">
                  <p className="ew-prod-docunest__card-label">The Solution</p>
                  <p className="ew-prod-docunest__card-text">
                    DocuNest.AI uses domain-specific AI to extract, classify, and enrich
                    every document — making your entire archive instantly searchable and
                    audit-ready.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right — image (order-1 on mobile, order-2 on md) */}
          <Reveal delay={200}>
            <div className="ew-prod-docunest__img-wrap">
              <img
                className="ew-prod-docunest__img"
                src={docunestWorkingImg}
                alt="DocuNest.AI document intelligence"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* ── Section 3: Cognitive Capabilities ───────────────────── */}
    <section style={{ padding: '0 1.5rem', marginBottom: '0' }}>
      <div style={{ maxWidth: 'var(--max-w)', margin: '0 auto' }}>
        <div className="ew-prod-cognitive">
          <div className="ew-prod-cognitive__grid">
            {/* Left */}
            <div className="ew-prod-cognitive__left">
              <Reveal>
                <p className="ew-prod-cognitive__eyebrow">Intelligence at Work</p>
                <h4 className="ew-prod-cognitive__heading">Cognitive Capabilities</h4>
              </Reveal>
              <div className="ew-prod-cognitive__cards">
                <Reveal delay={100}>
                  <div className="ew-prod-cognitive__card">
                    <div className="ew-prod-cognitive__icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3"/>
                      </svg>
                    </div>
                    <div>
                      <p className="ew-prod-cognitive__card-title">Advanced OCR &amp; NER</p>
                      <p className="ew-prod-cognitive__card-body">
                        Industry-tuned optical character recognition and named entity
                        recognition that understands energy-specific terminology, units,
                        and document structures with exceptional accuracy.
                      </p>
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={200}>
                  <div className="ew-prod-cognitive__card">
                    <div className="ew-prod-cognitive__icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 8V4H8"/>
                        <rect width="16" height="12" x="4" y="8" rx="2"/>
                        <path d="M2 14h2"/>
                        <path d="M20 14h2"/>
                        <path d="M15 13v2"/>
                        <path d="M9 13v2"/>
                      </svg>
                    </div>
                    <div>
                      <p className="ew-prod-cognitive__card-title">Domain-Specific LLM</p>
                      <p className="ew-prod-cognitive__card-body">
                        A large language model fine-tuned on upstream and midstream
                        energy data — capable of answering complex technical queries
                        directly from your document corpus.
                      </p>
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={300}>
                  <div className="ew-prod-cognitive__card">
                    <div className="ew-prod-cognitive__icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <path d="M3 15h6"/>
                        <path d="M3 10h6"/>
                        <path d="M3 20h6"/>
                      </svg>
                    </div>
                    <div>
                      <p className="ew-prod-cognitive__card-title">Adaptive Metadata Tagging</p>
                      <p className="ew-prod-cognitive__card-body">
                        Automatically enriches every ingested document with contextual
                        metadata — well names, formations, dates, regulatory codes —
                        aligned to OSDU and custom taxonomy standards.
                      </p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>

            {/* Right */}
            <div className="ew-prod-cognitive__right">
              <img
                className="ew-prod-cognitive__bg"
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1200"
                alt="Deep learning engine"
              />
              <div className="ew-prod-cognitive__gradient" />
              <div className="ew-prod-cognitive__badge">
                <Reveal>
                  <div className="ew-prod-cognitive__pulse">
                    <div className="ew-prod-cognitive__pulse-inner">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2a10 10 0 1 0 10 10"/>
                        <path d="M12 6v6l4 2"/>
                      </svg>
                    </div>
                  </div>
                  <p className="ew-prod-cognitive__badge-title">Deep Learning Engine</p>
                  <p className="ew-prod-cognitive__badge-body">
                    Continuously improving models trained on millions of energy
                    documents for unmatched extraction accuracy.
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Section 4: Case Studies ──────────────────────────────── */}
    <section style={{ padding: '6rem 1.5rem 0' }}>
      <div style={{ maxWidth: 'var(--max-w)', margin: '0 auto' }}>
        <Reveal>
          <div className="ew-prod-cases__grid">
            {/* Card 1 — Suriname PE */}
            <div className="ew-prod-case">
              <img
                className="ew-prod-case__img"
                src={documentsImg}
                alt="Suriname PE asset data liberation"
              />
              <div className="ew-prod-case__mask" />
              <div className="ew-prod-case__content">
                <p className="ew-prod-case__label">Suriname PE</p>
                <p className="ew-prod-case__title">Asset Data Liberation</p>
                <p className="ew-prod-case__stat">~80%</p>
                <p className="ew-prod-case__desc">
                  Of legacy documents structured and migrated to OSDU within 6 weeks.
                </p>
              </div>
            </div>

            {/* Card 2 — Brazil PE */}
            <div className="ew-prod-case">
              <img
                className="ew-prod-case__img"
                src="https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&q=80&w=800"
                alt="Brazil PE productivity gain"
              />
              <div className="ew-prod-case__mask" />
              <div className="ew-prod-case__content">
                <p className="ew-prod-case__label">Brazil PE</p>
                <p className="ew-prod-case__title">Productivity Gain</p>
                <p className="ew-prod-case__stat">~85%</p>
                <p className="ew-prod-case__desc">
                  Reduction in manual document review time across engineering teams.
                </p>
              </div>
            </div>

            {/* Card 3 — PVT Remediation */}
            <div className="ew-prod-case">
              <img
                className="ew-prod-case__img"
                src={qcImg}
                alt="PVT remediation quality and traceability"
              />
              <div className="ew-prod-case__mask" />
              <div className="ew-prod-case__content">
                <p className="ew-prod-case__label">PVT Remediation</p>
                <p className="ew-prod-case__title">Quality &amp; Traceability</p>
                <p className="ew-prod-case__stat">~90%</p>
                <p className="ew-prod-case__desc">
                  Accuracy in automated PVT report extraction and quality validation.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* ── Section 5: EnergyStudio ──────────────────────────────── */}
    <section className="ew-prod-studio">
      <div className="ew-prod-studio__card">
        <img
          className="ew-prod-studio__bg"
          src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=2000"
          alt="EnergyStudio background texture"
        />
        <div className="ew-prod-studio__overlay" />
        <div className="ew-prod-studio__inner">
          {/* Header */}
          <Reveal>
            <div className="ew-prod-studio__header">
              <p className="ew-prod-studio__eyebrow">Unified Operating System</p>
              <h2 className="ew-prod-studio__heading">EnergyStudio</h2>
              <p className="ew-prod-studio__sub">
                An OSDU-native operating platform that unifies data management,
                document AI, 3D lifecycle visualization, and project intelligence into
                a single, collaborative environment — built for the pace of modern
                energy operations.
              </p>
            </div>
          </Reveal>

          {/* Metrics */}
          <Reveal delay={100}>
            <div className="ew-prod-studio__metrics">
              <div className="ew-prod-studio__metric">
                <p className="ew-prod-studio__metric-val">70%</p>
                <p className="ew-prod-studio__metric-label">Less Data Prep</p>
              </div>
              <div className="ew-prod-studio__metric">
                <p className="ew-prod-studio__metric-val">30%</p>
                <p className="ew-prod-studio__metric-label">Faster Projects</p>
              </div>
              <div className="ew-prod-studio__metric">
                <p className="ew-prod-studio__metric-val">+5%</p>
                <p className="ew-prod-studio__metric-label">Production Uptime</p>
              </div>
              <div className="ew-prod-studio__metric">
                <p className="ew-prod-studio__metric-val">30%</p>
                <p className="ew-prod-studio__metric-label">Fewer Dups</p>
              </div>
              <div className="ew-prod-studio__metric">
                <p className="ew-prod-studio__metric-val">Lower</p>
                <p className="ew-prod-studio__metric-label">TCO</p>
              </div>
            </div>
          </Reveal>

          {/* Modules */}
          <div className="ew-prod-studio__modules">
            <Reveal delay={0}>
              <div className="ew-prod-studio__module">
                <div className="ew-prod-studio__module-header">
                  <h3 className="ew-prod-studio__module-title">OSDU Core</h3>
                  <span className="ew-prod-studio__module-num">01</span>
                </div>
                <p className="ew-prod-studio__module-body">
                  The foundational data layer — fully compliant with OSDU standards,
                  providing schema governance, data lineage, and real-time enrichment
                  across all ingested assets.
                </p>
                <div className="ew-prod-studio__tags">
                  <span className="ew-prod-studio__tag">Standard Schemas</span>
                  <span className="ew-prod-studio__tag">Auto-Enrichment</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="ew-prod-studio__module">
                <div className="ew-prod-studio__module-header">
                  <h3 className="ew-prod-studio__module-title">Document AI</h3>
                  <span className="ew-prod-studio__module-num">02</span>
                </div>
                <p className="ew-prod-studio__module-body">
                  Powered by DocuNest.AI, this module transforms unstructured documents
                  into indexed, queryable knowledge — with semantic search and
                  intelligent auto-tagging built in.
                </p>
                <div className="ew-prod-studio__tags">
                  <span className="ew-prod-studio__tag">Semantic Search</span>
                  <span className="ew-prod-studio__tag">Auto-Tagging</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="ew-prod-studio__module">
                <div className="ew-prod-studio__module-header">
                  <h3 className="ew-prod-studio__module-title">3D Lifecycle</h3>
                  <span className="ew-prod-studio__module-num">03</span>
                </div>
                <p className="ew-prod-studio__module-body">
                  Full asset lifecycle management with immersive XR visualization and
                  subsurface digital twin capabilities — bridging the physical and
                  digital for engineering and operations teams.
                </p>
                <div className="ew-prod-studio__tags">
                  <span className="ew-prod-studio__tag">XR Immersive</span>
                  <span className="ew-prod-studio__tag">Subsurface Twin</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={450}>
              <div className="ew-prod-studio__module">
                <div className="ew-prod-studio__module-header">
                  <h3 className="ew-prod-studio__module-title">Synergy Engine</h3>
                  <span className="ew-prod-studio__module-num">04</span>
                </div>
                <p className="ew-prod-studio__module-body">
                  An intelligent project coordination layer that synchronises milestones,
                  tracks dependencies, and provides live Gantt visibility across all
                  active workstreams and teams.
                </p>
                <div className="ew-prod-studio__tags">
                  <span className="ew-prod-studio__tag">Milestone Tracking</span>
                  <span className="ew-prod-studio__tag">Live Gantt Sync</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* ── Section 6: Industry Friction ────────────────────────── */}
    <section className="ew-prod-friction">
      <div className="ew-prod-friction__inner">
        {/* Left — image */}
        <Reveal>
          <div className="ew-prod-friction__img-wrap">
            <img
              className="ew-prod-friction__img"
              src={energyWindImg}
              alt="Solving friction points in the energy industry"
            />
            <div className="ew-prod-friction__img-overlay" />
          </div>
        </Reveal>

        {/* Right — numbered list */}
        <Reveal delay={200}>
          <div>
            <p className="ew-prod-friction__eyebrow">Market Context</p>
            <h2 className="ew-prod-friction__heading">
              Solving the Friction Points of Energy
            </h2>
            <div className="ew-prod-friction__list">
              <div className="ew-prod-friction__item">
                <span className="ew-prod-friction__num">01</span>
                <div>
                  <p className="ew-prod-friction__item-title">Data Silos</p>
                  <p className="ew-prod-friction__item-body">
                    Critical subsurface and operational data remains fragmented across
                    legacy systems, spreadsheets, and isolated databases — making
                    cross-functional decision-making slow and error-prone.
                  </p>
                </div>
              </div>
              <div className="ew-prod-friction__item">
                <span className="ew-prod-friction__num">02</span>
                <div>
                  <p className="ew-prod-friction__item-title">Unstructured Chaos</p>
                  <p className="ew-prod-friction__item-body">
                    Over 80% of energy data is unstructured — trapped in PDFs, scanned
                    logs, and handwritten reports that no existing system can
                    automatically process at scale.
                  </p>
                </div>
              </div>
              <div className="ew-prod-friction__item">
                <span className="ew-prod-friction__num">03</span>
                <div>
                  <p className="ew-prod-friction__item-title">Slow Handovers</p>
                  <p className="ew-prod-friction__item-body">
                    Asset handovers and project transitions are bottlenecked by manual
                    data collation — costing weeks of productivity and introducing
                    significant risk of information loss.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* ── Section 7: Final CTA ─────────────────────────────────── */}
    <section className="ew-prod-cta">
      <div className="ew-prod-cta__card">
        {/* Left */}
        <div className="ew-prod-cta__left">
          <div className="ew-prod-cta__glow" />
          <Reveal>
            <p className="ew-prod-cta__eyebrow">Get Started Today</p>
            <h2 className="ew-prod-cta__heading">Modernize Your Operations</h2>
            <p className="ew-prod-cta__sub">
              See how DocuNest.AI and EnergyStudio can transform your data operations,
              accelerate project delivery, and unlock intelligence from your entire
              document archive.
            </p>
            <button className="ew-prod-cta__btn">Request a Full Demo</button>
          </Reveal>
        </div>

        {/* Right */}
        <div className="ew-prod-cta__right">
          <img
            className="ew-prod-cta__img"
            src={analyticsDashboardImg}
            alt="Modernize your operations"
          />
          <div className="ew-prod-cta__img-gradient" />
        </div>
      </div>
    </section>
  </>
);

export default Products;
